// Product Recommendation Cross-Sells JavaScript
// Works with the existing product-recommendations custom element
// 
// Updated to leverage theme's existing cart utilities for consistency:
// - Uses theme's standard dispatchCustomEvent function
// - Follows theme's cart operation patterns
// - Implements consistent error handling and loading states
// - Leverages existing section rendering utilities
//
// CSS is now in product-recommendation-cross-sells.css file

/**
 * CrossSellManager - Manages cross-sell product functionality
 * Encapsulates all cross-sell related operations for better testability and maintainability
 */
class CrossSellManager {
  constructor() {
    this.selectors = {
      fallbackContainer: '.product-recommendation-cross-sells-fallback',
      recommendationsContainer: '.product-recommendation-cross-sells-container:not(.product-recommendation-cross-sells-fallback)',
      actualContent: '#product-recommendation-cross-sells, #product-recommendation-cross-sells-fallback-content',
      loadingPlaceholder: '[data-loading-placeholder]',
      totalElement: '#product-recommendation-cross-sells-total',
      form: '#product-recommendation-cross-sells-form',
      submitButton: '#product-recommendation-cross-sells-add-to-cart',
      productRecommendations: 'product-recommendations',
      cartDrawerToggle: '#cart-drawer-toggle'
    };
    
    this.classes = {
      loading: 'loading',
      hidden: 'tw:hidden',
      block: 'tw:block',
      opacity100: 'tw:opacity-100',
      opacity0: 'tw:opacity-0'
    };
    
    this.timeouts = {
      contentTransition: 250,
      fadeOut: 500,
      fallbackDetection: 1500,
      cartDrawerOpen: 500,
      buttonReset: 1000,
      errorReset: 2000
    };
  }

  /**
   * Initialize the cross-sell manager
   */
  init() {
    this.setupEventListeners();
    this.setupRecommendationObserver();
    this.setupFallbackTimeout();
    this.initializeTotalCalculation();
  }

  // ==================== DOM QUERY UTILITIES ====================

  /**
   * Get fallback container element
   * @returns {Element|null}
   */
  getFallbackContainer() {
    return document.querySelector(this.selectors.fallbackContainer);
  }

  /**
   * Get recommendations container element
   * @returns {Element|null}
   */
  getRecommendationsContainer() {
    return document.querySelector(this.selectors.recommendationsContainer);
  }

  /**
   * Get actual content element within fallback container
   * @returns {Element|null}
   */
  getActualContent() {
    const container = this.getFallbackContainer();
    return container ? container.querySelector(this.selectors.actualContent) : null;
  }

  /**
   * Get loading placeholder element
   * @returns {Element|null}
   */
  getLoadingPlaceholder() {
    const container = this.getFallbackContainer();
    return container ? container.querySelector(this.selectors.loadingPlaceholder) : null;
  }

  /**
   * Get total price element from the visible container
   * @returns {Element|null}
   */
  getTotalElement() {
    // Check which container is visible and get its total element
    const recommendationsContainer = this.getRecommendationsContainer();
    const fallbackContainer = this.getFallbackContainer();
    
    // Check if recommendations container exists and is visible
    if (recommendationsContainer && !recommendationsContainer.classList.contains(this.classes.hidden)) {
      return recommendationsContainer.querySelector('#product-recommendation-cross-sells-total');
    }
    
    // Check if fallback container exists and is visible
    if (fallbackContainer && !fallbackContainer.classList.contains(this.classes.hidden)) {
      return fallbackContainer.querySelector('#product-recommendation-cross-sells-total');
    }
    
    // Fallback to document-level query if neither container is found
    return document.getElementById('product-recommendation-cross-sells-total');
  }

  /**
   * Get recommended items (non-fallback) from recommendations container only
   * @returns {NodeList}
   */
  getRecommendedItems() {
    const recommendationsContainer = this.getRecommendationsContainer();
    if (!recommendationsContainer) {
      return [];
    }
    return recommendationsContainer.querySelectorAll('.product-recommendation-cross-sells-item[data-product-index]:not([data-product-index="0"]):not(.fallback-product-item)');
  }

  // ==================== UI STATE MANAGEMENT ====================

  /**
   * Show loading state for fallback container
   */
  showLoadingState() {
    const container = this.getFallbackContainer();
    if (!container) return;
    
    // Add loading class to container
    container.classList.add(this.classes.loading);
    
    // Show the loading placeholder
    const loadingPlaceholder = this.getLoadingPlaceholder();
    if (loadingPlaceholder) {
      loadingPlaceholder.classList.remove(this.classes.hidden);
      loadingPlaceholder.classList.add(this.classes.block, this.classes.opacity100);
    }
    
    // Hide the actual content initially
    const actualContent = this.getActualContent();
    if (actualContent) {
      actualContent.classList.add(this.classes.hidden);
      actualContent.classList.remove('loaded');
    }
  }

  /**
   * Initialize fallback loading state
   */
  initializeFallbackLoading() {
    const fallbackContainer = this.getFallbackContainer();
    if (!fallbackContainer) return;
    
    // Ensure actual content is hidden first
    const actualContent = this.getActualContent();
    if (actualContent) {
      actualContent.classList.add(this.classes.hidden);
      actualContent.classList.remove(this.classes.opacity100);
      actualContent.classList.add(this.classes.opacity0);
    }
    
    // Show the fallback container with loading state
    fallbackContainer.classList.remove(this.classes.hidden);
    fallbackContainer.classList.add(this.classes.block);
    this.showLoadingState();
    
    // Show the actual content after transition time
    setTimeout(() => {
      this.showContent();
    }, this.timeouts.contentTransition);
  }

  /**
   * Show content and hide loading state
   */
  showContent() {
    const container = this.getFallbackContainer();
    if (!container) return;
    
    // Show the actual content first (but keep it transparent)
    const actualContent = this.getActualContent();
    if (actualContent) {
      actualContent.classList.remove(this.classes.hidden);
    }
    
    // Start fade out of loading placeholder
    const loadingPlaceholder = this.getLoadingPlaceholder();
    if (loadingPlaceholder) {
      loadingPlaceholder.classList.remove(this.classes.opacity100);
      loadingPlaceholder.classList.add(this.classes.opacity0);
    }
    
    // After placeholder fades out, fade in the content
    setTimeout(() => {
      this.finalizeContentDisplay(container, loadingPlaceholder, actualContent);
    }, this.timeouts.fadeOut);
  }

  /**
   * Finalize content display after fade transitions
   * @param {Element} container 
   * @param {Element} loadingPlaceholder 
   * @param {Element} actualContent 
   */
  finalizeContentDisplay(container, loadingPlaceholder, actualContent) {
    // Remove loading class
    container.classList.remove(this.classes.loading);
    
    // Hide the loading placeholder completely
    if (loadingPlaceholder) {
      loadingPlaceholder.classList.add(this.classes.hidden);
      loadingPlaceholder.classList.remove(this.classes.opacity100);
      loadingPlaceholder.classList.add(this.classes.opacity0);
    }
    
    // Fade in the actual content
    if (actualContent) {
      actualContent.classList.remove(this.classes.opacity0);
      actualContent.classList.add(this.classes.opacity100);
    }
    
    // Only ensure container is visible if it's the fallback container
    if (container.classList.contains('product-recommendation-cross-sells-fallback')) {
      container.classList.remove(this.classes.hidden);
      container.classList.add(this.classes.block);
    }
  }

  /**
   * Determine what content to show (recommendations vs fallback)
   */
  determineContentToShow() {
    const recommendedItems = this.getRecommendedItems();
    const fallbackContainer = this.getFallbackContainer();
    const recommendationsContainer = this.getRecommendationsContainer();
    
    if (recommendedItems.length > 0) {
      this.showRecommendations(fallbackContainer);
    } else if (fallbackContainer) {
      this.showFallback(fallbackContainer, recommendationsContainer);
    }
  }

  /**
   * Show recommendations and hide fallback
   * @param {Element} fallbackContainer 
   */
  showRecommendations(fallbackContainer) {
    if (fallbackContainer) {
      fallbackContainer.classList.add(this.classes.hidden);
      fallbackContainer.classList.remove(this.classes.block);
    }
  }

  /**
   * Show fallback and hide recommendations
   * @param {Element} fallbackContainer 
   * @param {Element} recommendationsContainer 
   */
  showFallback(fallbackContainer, recommendationsContainer) {
    if (recommendationsContainer) {
      recommendationsContainer.classList.add(this.classes.hidden);
    }
    
    // Ensure fallback is visible and has loading state
    if (fallbackContainer.classList.contains(this.classes.hidden)) {
      fallbackContainer.classList.remove(this.classes.hidden);
      fallbackContainer.classList.add(this.classes.block);
      this.showLoadingState();
      setTimeout(() => {
        this.showContent();
      }, this.timeouts.contentTransition);
    }
  }

  // ==================== PRICE CALCULATION ====================

  /**
   * Update total price based on selected items
   */
  updateTotal() {
    const totalElement = this.getTotalElement();
    if (!totalElement) return;

    const total = this.calculateTotal();
    totalElement.textContent = `$${total.toFixed(2)}`;
  }

  /**
   * Calculate total price from selected items
   * @returns {number}
   */
  calculateTotal() {
    let total = 0;
    
    // Only get items from visible forms (not hidden fallback/recommendation containers)
    const forms = document.querySelectorAll('#product-recommendation-cross-sells-form');
    
    forms.forEach((form, formIndex) => {
      // Skip hidden forms - check multiple possible container types
      const container = form.closest('.product-recommendation-cross-sells-container, #product-recommendation-cross-sells, .product-recommendation-cross-sells-fallback');
      const isHidden = container && (
        container.classList.contains('tw:hidden') || 
        container.classList.contains('hidden') ||
        getComputedStyle(container).display === 'none' ||
        getComputedStyle(container).visibility === 'hidden' ||
        container.offsetParent === null
      );
      
      if (isHidden) {
        return;
      }
      
      const checkedItems = form.querySelectorAll('input[name="product-check"]:checked');
      
      checkedItems.forEach(checkbox => {
        const productIndex = checkbox.getAttribute('data-product-index');
        
        const productItem = form.querySelector(`.product-recommendation-cross-sells-item[data-product-index="${productIndex}"]`);
        
        if (productItem) {
          const price = this.getItemPrice(productItem);
          total += price;
        }
      });
    });

    return total;
  }

  /**
   * Get price for a specific product item
   * @param {Element} productItem 
   * @returns {number}
   */
  getItemPrice(productItem) {
    // Get the selected variant price
    const selectedVariant = productItem.querySelector('input[type="radio"]:checked');
    if (selectedVariant) {
      const price = parseFloat(selectedVariant.getAttribute('data-variant-price')) / 100;
      return price;
    } else {
      // Fallback to default price
      const defaultPrice = parseFloat(productItem.getAttribute('data-default-price')) / 100;
      return defaultPrice;
    }
  }

  /**
   * Update product image for variant change
   * @param {string} productIndex 
   * @param {string} imageUrl 
   */
  updateProductImage(productIndex, imageUrl) {
    const imageElement = document.querySelector(`.product-variant-image[data-product-index="${productIndex}"]`);
    if (imageElement && imageUrl) {
      imageElement.src = imageUrl;
    }
  }

  /**
   * Update variant text for variant change
   * @param {string} productIndex 
   * @param {string} variantTitle 
   */
  updateVariantText(productIndex, variantTitle) {
    const productItem = document.querySelector(`.product-recommendation-cross-sells-item[data-product-index="${productIndex}"]`);
    if (productItem) {
      const labelElement = productItem.querySelector('.form__label__value');
      if (labelElement) {
        labelElement.textContent = variantTitle;
      }
    }
  }

  /**
   * Update displayed price for variant change
   * @param {string} productIndex 
   * @param {number} variantPrice - Price in cents
   */
  updateDisplayedPrice(productIndex, variantPrice) {
    const priceElement = document.querySelector(`.product-price[data-product-index="${productIndex}"]`);
    if (priceElement) {
      const priceInDollars = variantPrice / 100;
      priceElement.textContent = `$${priceInDollars.toFixed(2)}`;
    }
  }

  // ==================== CART OPERATIONS ====================

  /**
   * Check if theme's cart utilities are available
   * @returns {boolean}
   */
  isThemeCartAvailable() {
    return typeof theme !== 'undefined' && 
           theme.routes && 
           theme.routes.cart_add_url &&
           typeof dispatchCustomEvent === 'function';
  }

  /**
   * Get selected products from form
   * @param {Element} form 
   * @returns {Array}
   */
  getSelectedProducts(form) {
    const checkedProducts = form.querySelectorAll('input[name="product-check"]:checked');
    const products = [];

    checkedProducts.forEach(checkbox => {
      const productIndex = checkbox.getAttribute('data-product-index');
      const productItem = form.querySelector(`[data-product-index="${productIndex}"]`);
      
      if (productItem) {
        const selectedVariant = productItem.querySelector('input[type="radio"]:checked');
        if (selectedVariant) {
          products.push({
            variantId: selectedVariant.value,
            quantity: 1
          });
        }
      }
    });

    return products;
  }

  /**
   * Add items to cart sequentially using theme-standard patterns
   * @param {Array} products 
   * @param {Element} submitButton 
   */
  async addItemsSequentially(products, submitButton) {
    try {
      for (let i = 0; i < products.length; i++) {
        const product = products[i];
        const data = await this.addSingleItemToCart(product);
        
        // Use theme's standard event dispatching
        this.dispatchCartItemAddedEvent(data);
        
        // Update cart sections if this is the last item
        if (i === products.length - 1 && data.sections) {
          this.renderContents(data);
        }
      }

      this.handleAddToCartSuccess(submitButton);
    } catch (error) {
      this.handleAddToCartError(submitButton, error);
    }
  }

  /**
   * Add a single item to cart using theme-standard patterns
   * @param {Object} product 
   * @returns {Promise<Object>}
   */
  async addSingleItemToCart(product) {
    if (!this.isThemeCartAvailable()) {
      throw new Error('Theme cart utilities not available');
    }

    const config = {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      }
    };

    const formData = this.createCartFormData(product);
    config.body = formData;
    
    const response = await fetch(theme.routes.cart_add_url, config);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    
    if (data.status) {
      throw new Error(data.description || 'Failed to add item to cart');
    }

    return data;
  }

  /**
   * Create FormData for cart add request using theme-standard pattern
   * Note: Attribution tracking is handled via hidden input in the form
   * @param {Object} product 
   * @returns {FormData}
   */
  createCartFormData(product) {
    const formData = new FormData();
    formData.append('id', product.variantId);
    formData.append('quantity', product.quantity);
    formData.append('sections', this.getSectionsToRender().map(section => section.section));
    formData.append('sections_url', window.location.pathname);
    
    // Get the form element to include hidden inputs (like attribution tracking)
    const form = document.querySelector(this.selectors.form);
    if (form) {
      const attributionInput = form.querySelector('input[name="properties[_attribution]"]');
      if (attributionInput) {
        formData.append('properties[_attribution]', attributionInput.value);
      }
    }
    
    return formData;
  }

  /**
   * Dispatch cart item added event using theme's standard function
   * @param {Object} data 
   */
  dispatchCartItemAddedEvent(data) {
    const product = data.hasOwnProperty('items') ? data.items[0] : data;
    
    // Use theme's standard dispatchCustomEvent function
    if (typeof dispatchCustomEvent === 'function') {
      dispatchCustomEvent('cart:item-added', { product });
    } else {
      // Fallback to standard CustomEvent if dispatchCustomEvent is not available
      document.documentElement.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { product },
        bubbles: true
      }));
    }
  }

  /**
   * Handle successful add to cart using theme-standard patterns
   * @param {Element} submitButton 
   */
  handleAddToCartSuccess(submitButton) {
    if (submitButton) {
      const buttonText = submitButton.querySelector('.single-add-to-cart-button--text');
      if (buttonText) {
        buttonText.textContent = 'ADDED!';
        setTimeout(() => {
          buttonText.textContent = 'ADD TO CART';
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
        }, this.timeouts.buttonReset);
      }
    }
    
    // Use theme's standard cart refresh event
    if (typeof dispatchCustomEvent === 'function') {
      dispatchCustomEvent('cart:refresh');
    } else {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
    
    // Open cart drawer using theme's standard pattern
    setTimeout(() => {
      const cartDrawerToggle = document.getElementById('cart-drawer-toggle');
      if (cartDrawerToggle) {
        cartDrawerToggle.click();
      }
    }, this.timeouts.cartDrawerOpen);
  }

  /**
   * Handle add to cart error using theme-standard patterns
   * @param {Element} submitButton 
   * @param {Error} error 
   */
  handleAddToCartError(submitButton, error) {
    if (submitButton) {
      const buttonText = submitButton.querySelector('.single-add-to-cart-button--text');
      if (buttonText) {
        buttonText.textContent = 'ERROR - TRY AGAIN';
        submitButton.classList.remove('loading');
        submitButton.removeAttribute('aria-disabled');
        setTimeout(() => {
          buttonText.textContent = 'ADD TO CART';
        }, this.timeouts.errorReset);
      }
    }
  }

  /**
   * Get sections to render for cart updates using theme-standard pattern
   * @returns {Array}
   */
  getSectionsToRender() {
    return [{
      id: 'Cart',
      section: 'main-cart',
      selector: '.thb-cart-form'
    },
    {
      id: 'Cart-Drawer',
      section: 'cart-drawer',
      selector: '.cart-drawer'
    },
    {
      id: 'cart-drawer-toggle',
      section: 'cart-bubble',
      selector: '.thb-item-count'
    }];
  }

  /**
   * Render cart contents after update using theme-standard patterns
   * @param {Object} parsedState 
   */
  renderContents(parsedState) {
    this.getSectionsToRender().forEach(section => {
      if (!document.getElementById(section.id)) {
        return;
      }
      
      const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
      
      if (parsedState.sections && parsedState.sections[section.section]) {
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
      }

      // Use theme's standard cart drawer methods
      if (section.id === 'Cart-Drawer') {
        const cartDrawer = document.getElementById('Cart-Drawer');
        if (cartDrawer) {
          cartDrawer.notesToggle?.();
          cartDrawer.removeProductEvent?.();
        }
      }
      
      // Use theme's standard Cart class for main cart
      if (section.id === 'Cart' && typeof Cart !== 'undefined') {
        new Cart().renderContents(parsedState);
      }
    });
  }

  /**
   * Get section inner HTML using theme-standard pattern
   * @param {string} html 
   * @param {string} selector 
   * @returns {string}
   */
  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const element = doc.querySelector(selector);
    return element ? element.innerHTML : '';
  }

  // ==================== EVENT HANDLING ====================

  /**
   * Setup all event listeners
   */
  setupEventListeners() {
    this.setupVariantChangeListener();
    this.setupFormSubmitListener();
    this.setupButtonClickListener();
  }

  /**
   * Setup variant change event listener
   */
  setupVariantChangeListener() {
    document.addEventListener('change', (e) => {
      if (e.target.matches('input[type="radio"]')) {
        this.handleVariantChange(e);
      }
      
      if (e.target.matches('input[name="product-check"]')) {
        this.updateTotal();
      }
    });
  }

  /**
   * Handle variant change
   * @param {Event} e 
   */
  handleVariantChange(e) {
    const productIndex = e.target.getAttribute('data-product-index');
    const variantTitle = e.target.getAttribute('data-variant-title');
    const variantImage = e.target.getAttribute('data-variant-image');
    const variantPrice = e.target.getAttribute('data-variant-price');
    
    this.updateVariantText(productIndex, variantTitle);
    this.updateProductImage(productIndex, variantImage);
    if (variantPrice) {
      this.updateDisplayedPrice(productIndex, parseFloat(variantPrice));
    }
    this.updateTotal();
  }

  /**
   * Setup form submit event listener
   */
  setupFormSubmitListener() {
    document.addEventListener('submit', (e) => {
      if (e.target.id === 'product-recommendation-cross-sells-form') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        this.handleFormSubmit(e.target);
      }
    });
  }

  /**
   * Handle form submission
   * @param {Element} form 
   */
  handleFormSubmit(form) {
    const submitButton = form.querySelector(this.selectors.submitButton);
    
    if (!form) return;

    const products = this.getSelectedProducts(form);
    if (products.length === 0) return;

    this.setLoadingState(submitButton);
    this.addItemsSequentially(products, submitButton);
  }

  /**
   * Setup button click event listener (backup)
   */
  setupButtonClickListener() {
    document.addEventListener('click', (e) => {
      if (e.target.closest(this.selectors.submitButton)) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        this.handleButtonClick(e.target.closest(this.selectors.submitButton));
      }
    });
  }

  /**
   * Handle button click
   * @param {Element} button 
   */
  handleButtonClick(button) {
    const form = button.closest('form');
    
    if (!form || form.id !== 'product-recommendation-cross-sells-form') {
      return;
    }

    const products = this.getSelectedProducts(form);
    if (products.length === 0) return;

    this.setLoadingState(button);
    this.addItemsSequentially(products, button);
  }

  /**
   * Set loading state for button using theme-standard patterns
   * @param {Element} button 
   */
  setLoadingState(button) {
    if (button) {
      const buttonText = button.querySelector('.single-add-to-cart-button--text');
      if (buttonText) {
        buttonText.textContent = 'ADDING...';
      }
      button.setAttribute('aria-disabled', true);
      button.classList.add('loading');
    }
  }

  /**
   * Setup recommendation observer
   */
  setupRecommendationObserver() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target;
          if (target.classList.contains('product-recommendations--loaded')) {
            setTimeout(() => {
              this.determineContentToShow();
              // Don't call showContent() here - it's for fallback loading state only
              // determineContentToShow() handles visibility correctly
              this.updateTotal();
            }, 100);
          }
        }
      });
    });

    const productRecommendationsElement = document.querySelector(this.selectors.productRecommendations);
    if (productRecommendationsElement) {
      observer.observe(productRecommendationsElement, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
  }

  /**
   * Setup fallback timeout
   */
  setupFallbackTimeout() {
    setTimeout(() => {
      const recommendationsContainer = this.getRecommendationsContainer();
      
      // Only show fallback if no recommendations container exists or if it's empty
      if (!recommendationsContainer || recommendationsContainer.children.length === 0) {
        this.determineContentToShow();
        this.updateTotal();
      }
    }, this.timeouts.fallbackDetection);
  }

  /**
   * Initialize total calculation with timeouts
   */
  initializeTotalCalculation() {
    setTimeout(() => this.updateTotal(), 1000);
    setTimeout(() => this.updateTotal(), 2000);
    setTimeout(() => this.updateTotal(), 3000);
  }
}

// Initialize the CrossSellManager when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  const crossSellManager = new CrossSellManager();
  crossSellManager.init();
});